import * as React from "react";
import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import {
  Briefcase,
  Calendar,
  Check,
  ChevronDown,
  FileText,
  Folder,
  type LucideIcon,
  Mail,
  MessageSquare,
  Wallet,
} from "lucide-react";

import { cn } from "../../lib/cn";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../primitives/popover";

/**
 * ConnectionPicker — multi-source account selector (Ando v18, May 2026).
 *
 * Pattern reference: Ando `.filter-chip` (see ``SubTabBar`` →
 * ``SubTabChip``) — a single chip-pill acts as the trigger; clicking opens
 * a dropdown menu where every connected account can be toggled on/off.
 *
 * The chip pattern intentionally departs from the earlier Google-Calendar
 * "stacked avatar cluster" prototype: avatar clusters with saturated
 * vendor brand colours conflicted with Ando's warm-cream / ink-first
 * palette and broke the white-label promise (CLAUDE.md
 * `feedback_white_label_capability_naming.md`).
 *
 * Visual contract:
 * - Trigger      : `.filter-chip` chip-pill (text-[11px] py-[5px] px-3
 *                  rounded-full). Active = `bg-ink-1 text-white`;
 *                  Inactive = `bg-transparent text-ink-2 hover:bg-white/60`.
 * - Dropdown     : `shadow-md rounded-md w-72`, animated via `animate-el-in`.
 * - Row glyph    : monochrome Lucide icon (Mail / MessageSquare / Calendar
 *                  / Folder / Briefcase / FileText / Wallet) on
 *                  `bg-cream-deep text-ink-1` — NO saturated vendor fill.
 *                  When `avatar_url` is provided we render the real OAuth
 *                  profile picture inside the same 7×7 slot.
 * - Source theme : `resolveConnectionColor()` returns a semantic
 *                  `hsl(var(--domain-X))` CSS-var reference so consuming
 *                  cards (Calendar event tags, Conversation source dots,
 *                  Document chips, Finance pills) inherit the same
 *                  wayfinding palette as the rest of the workspace.
 *
 * Persistence + reconciliation live in the sibling ``useActiveConnections``
 * hook in apps/client. The component is presentation-only.
 *
 * Per CLAUDE.md "white-label naming OBBLIGATORIO": ``provider`` slugs flow
 * through opaque — vendor brand hexes are never hardcoded in the picker.
 * Per-row ``color_hex`` and the ``colorMap`` prop are kept for caller
 * overrides but the default mapping is *semantic*, not brand.
 */

// ---------------------------------------------------------------------------
// Local type contract — kept structurally compatible with
// ``@ando/core/domain.Connection`` but defined here so ``@ando/ui`` stays
// dependency-free of the domain package (DRY type, decoupled package).
// ---------------------------------------------------------------------------

export interface ConnectionPickerOption {
  /** Stable id — used as React key, localStorage entry, ARIA target. */
  readonly id: string;
  /** Vendor / brand slug — feeds the default ``colorMap``. */
  readonly provider: string;
  /** Human-readable account label (e.g. ``"andrea.bovinelli@gmail.com"``). */
  readonly account_label: string;
  /** Optional avatar URL. */
  readonly avatar_url?: string;
  /** Optional per-connection colour override (CSS hex / rgb / var). */
  readonly color_hex?: string;
  /** Optional secondary line — typically the account email. */
  readonly secondary_label?: string;
}

// ---------------------------------------------------------------------------
// Provider → domain semantic mapping
// ---------------------------------------------------------------------------

/**
 * Provider slug → Ando ``domain.*`` token key. This is the single source
 * of truth feeding both the source-theme colour (via
 * ``resolveConnectionColor``) and the row glyph (via
 * ``getProviderGlyph``). Adding a new vendor is a one-liner here — no
 * downstream surface has to change.
 *
 * Keep keys lowercase + snake_case to match BE connector slugs.
 */
const PROVIDER_TO_DOMAIN: Readonly<Record<string, string>> = {
  // Email / PEC
  gmail: "email",
  google_mail: "email",
  outlook: "email",
  outlook_mail: "email",
  aruba_pec: "email",
  infocert_pec: "email",
  // Messaging
  slack: "messaging",
  teams: "messaging",
  google_chat: "messaging",
  // Drive
  google_drive: "drive",
  onedrive: "drive",
  sharepoint: "drive",
  dropbox: "drive",
  box: "drive",
  // Calendar
  google_calendar: "calendar",
  outlook_calendar: "calendar",
  // OAuth-bundle vendor slugs that resolve to *calendar* when the surface
  // is a calendar (Calendar.tsx maps `google` → `google` for back-compat).
  google: "calendar",
  microsoft: "calendar",
  // Enterprise / CRM / Finance
  salesforce: "enterprise",
  hubspot: "enterprise",
  sap_b1: "enterprise",
  open_banking: "enterprise",
  fic: "enterprise",
  fatture_in_cloud: "enterprise",
  aruba_sdi: "enterprise",
  sdi_invoicing: "enterprise",
  cassetto_fiscale: "enterprise",
  // Filesystem (manual upload)
  manual: "filesystem",
  manual_upload: "filesystem",
};

/** Default neutral fallback when a provider slug is unknown. */
const DEFAULT_DOMAIN = "enterprise" as const;

/** Resolve the Ando domain token for a given provider slug. */
function providerToDomain(provider: string): string {
  return PROVIDER_TO_DOMAIN[provider] ?? DEFAULT_DOMAIN;
}

// ---------------------------------------------------------------------------
// Provider → glyph mapping (monochrome Lucide icons)
// ---------------------------------------------------------------------------

const DOMAIN_TO_GLYPH: Readonly<Record<string, LucideIcon>> = {
  email: Mail,
  messaging: MessageSquare,
  calendar: Calendar,
  drive: Folder,
  enterprise: Briefcase,
  filesystem: FileText,
  finance: Wallet,
};

/** Return the Lucide icon best suited to render a connection's source. */
export function getProviderGlyph(provider: string): LucideIcon {
  return DOMAIN_TO_GLYPH[providerToDomain(provider)] ?? Briefcase;
}

// ---------------------------------------------------------------------------
// Source-theme colour resolver
// ---------------------------------------------------------------------------

/**
 * @deprecated Prefer calling {@link resolveConnectionColor} directly. The
 * map is kept for back-compat so existing imports keep compiling. Values
 * are now semantic ``hsl(var(--domain-*))`` references — not vendor brand
 * hexes — so the picker respects the white-label promise.
 */
export const DEFAULT_PROVIDER_COLORS: Readonly<Record<string, string>> =
  Object.fromEntries(
    Object.entries(PROVIDER_TO_DOMAIN).map(
      ([slug, domain]) => [slug, `hsl(var(--domain-${domain}))`] as const,
    ),
  );

/**
 * Read the source-theme colour for a given option — per-row ``color_hex``
 * wins, then ``colorMap`` override, then the semantic
 * ``hsl(var(--domain-*))`` mapping derived from the provider slug, finally
 * a neutral ``--domain-enterprise`` fallback for unknown providers.
 *
 * Callers (Calendar event tags, Conversation source dots, Document chips,
 * Finance pills) pipe the returned string straight into a CSS `color` /
 * `background-color` rule — runtime tokens mean white-label tenants can
 * recolour every surface by overriding the `--domain-*` vars alone.
 */
export function resolveConnectionColor(
  option: ConnectionPickerOption,
  colorMap?: Readonly<Record<string, string>>,
): string {
  if (option.color_hex) return option.color_hex;
  if (colorMap?.[option.provider]) return colorMap[option.provider]!;
  const domain = providerToDomain(option.provider);
  return `hsl(var(--domain-${domain}))`;
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface ConnectionPickerProps {
  /** All connections the surface knows about. */
  readonly connections: readonly ConnectionPickerOption[];
  /** Ids currently active (a subset of ``connections.map(c => c.id)``). */
  readonly activeIds: readonly string[];
  /** Fired on every toggle / select-all / select-none. */
  readonly onChange: (ids: readonly string[]) => void;
  /**
   * Optional override map ``provider → colour``. Wins over the default
   * semantic mapping but loses to per-row ``color_hex``. Values can be
   * any CSS colour expression (hex, ``hsl()``, ``var()``).
   *
   * @deprecated Prefer overriding the ``--domain-*`` CSS variables at
   * tenant level — keeps the picker white-label.
   */
  readonly colorMap?: Readonly<Record<string, string>>;
  /** Optional trigger label — e.g. "Calendari", "Caselle", "Drive". */
  readonly label?: string;
  /** Override the default "Tutti" / "Nessuno" labels (i18n hook). */
  readonly allLabel?: string;
  readonly noneLabel?: string;
  /** Aria-label for the trigger button (defaults to ``label`` or "Account"). */
  readonly ariaLabel?: string;
  /** Forward className to the trigger button. */
  readonly className?: string;
  /** Forward className to the dropdown content. */
  readonly contentClassName?: string;
}

// ---------------------------------------------------------------------------
// Internals — small atoms kept inside the file (single-purpose, never
// exported individually) to keep the public surface tight.
// ---------------------------------------------------------------------------

interface ConnectionGlyphProps {
  readonly option: ConnectionPickerOption;
  readonly active: boolean;
}

/**
 * 7×7 slot housing either the OAuth profile picture (when ``avatar_url``
 * is set) or the monochrome Lucide glyph mapped from the provider. The
 * slot itself is `bg-cream-deep` so it sits flush with the warm surface
 * without any vendor-brand fill.
 */
const ConnectionGlyph: React.FC<ConnectionGlyphProps> = ({
  option,
  active,
}) => {
  const Icon = getProviderGlyph(option.provider);
  const hasAvatar = Boolean(option.avatar_url);
  return (
    <span
      aria-hidden
      className={cn(
        "inline-flex h-7 w-7 shrink-0 items-center justify-center overflow-hidden rounded-full bg-cream-deep text-ink-1",
        !active && "opacity-60",
      )}
    >
      {hasAvatar ? (
        <img
          src={option.avatar_url}
          alt=""
          className="h-full w-full rounded-full object-cover"
          referrerPolicy="no-referrer"
          loading="lazy"
        />
      ) : (
        <Icon className="h-3.5 w-3.5" strokeWidth={1.75} />
      )}
    </span>
  );
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const ConnectionPicker: React.FC<ConnectionPickerProps> = ({
  connections,
  activeIds,
  onChange,
  colorMap,
  label,
  allLabel,
  noneLabel,
  ariaLabel,
  className,
  contentClassName,
}) => {
  const { i18n } = useLingui();
  const resolvedAllLabel = allLabel ?? i18n._(msg`Tutti`);
  const resolvedNoneLabel = noneLabel ?? i18n._(msg`Nessuno`);
  const [open, setOpen] = React.useState(false);

  // Active-set membership lookup (O(1) per row).
  const activeSet = React.useMemo(
    () => new Set(activeIds),
    [activeIds],
  );

  const allActive =
    connections.length > 0 && activeSet.size === connections.length;
  const noneActive = activeSet.size === 0;

  // ── Handlers ───────────────────────────────────────────────────────────

  const toggle = React.useCallback(
    (id: string) => {
      const next = new Set(activeIds);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      // Preserve original ``connections`` order so the cluster stays stable.
      const ordered = connections
        .map((c) => c.id)
        .filter((cid) => next.has(cid));
      onChange(ordered);
    },
    [activeIds, connections, onChange],
  );

  const selectAll = React.useCallback(() => {
    onChange(connections.map((c) => c.id));
  }, [connections, onChange]);

  const selectNone = React.useCallback(() => {
    onChange([]);
  }, [onChange]);

  // ── Trigger summary ────────────────────────────────────────────────────

  const visibleActiveCount = activeSet.size;
  const totalCount = connections.length;
  const triggerLabel = label ?? i18n._(msg`Account`);
  const ariaSummary =
    ariaLabel ??
    (label
      ? i18n._(msg`${label} — ${visibleActiveCount} di ${totalCount} attivi`)
      : i18n._(msg`${visibleActiveCount} di ${totalCount} account attivi`));

  // The chip glyph: when a single surface flavour dominates (e.g. all
  // calendars) we lead with that domain's icon; otherwise we fall back to
  // the generic ``Briefcase`` neutral glyph.
  const TriggerIcon = React.useMemo<LucideIcon>(() => {
    if (connections.length === 0) return Briefcase;
    const domains = new Set(
      connections.map((c) => providerToDomain(c.provider)),
    );
    if (domains.size === 1) {
      const onlyDomain = [...domains][0]!;
      return DOMAIN_TO_GLYPH[onlyDomain] ?? Briefcase;
    }
    return Briefcase;
  }, [connections]);

  // ── Empty state ────────────────────────────────────────────────────────

  if (connections.length === 0) {
    return (
      <button
        type="button"
        disabled
        aria-disabled
        aria-label={ariaLabel ?? label ?? i18n._(msg`Nessun account`)}
        className={cn(
          // Disabled placeholder — keeps layout stable until the first
          // connection lands. Same chrome as the live trigger + "Filtri".
          "inline-flex h-8 shrink-0 items-center gap-1.5 rounded-md border border-hairline bg-transparent px-2.5 font-medium text-[11px] text-ink-3 leading-none opacity-60",
          className,
        )}
      >
        <TriggerIcon className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden />
        <span>{triggerLabel}</span>
        <span className="tabular-nums">· 0</span>
        <ChevronDown className="h-3 w-3" aria-hidden />
      </button>
    );
  }

  // ── Render ─────────────────────────────────────────────────────────────

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-haspopup="menu"
          aria-expanded={open}
          aria-label={ariaSummary}
          className={cn(
            // Ghost trigger aligned with the "Filtri" button beside it: same
            // h-8 / rounded-md / hairline border and orange-on-hover treatment
            // (border + text, no stark white fill). Open = warm orange tint.
            "group inline-flex h-8 shrink-0 items-center gap-1.5 rounded-md border border-hairline px-2.5 font-medium text-[11px] leading-none",
            "transition-colors duration-fast ease-smooth",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1",
            open
              ? "border-ando-orange/40 bg-ando-orange/15 text-ink-1"
              : "bg-transparent text-ink-2 hover:border-ando-orange/40 hover:text-ink-1",
            className,
          )}
        >
          <TriggerIcon
            className={cn(
              "h-3.5 w-3.5",
              open ? "text-ando-orange" : "text-ink-2",
            )}
            strokeWidth={1.75}
            aria-hidden
          />
          <span>{triggerLabel}</span>
          <span className="text-ink-3 tabular-nums" aria-hidden>
            · {i18n._(msg`${visibleActiveCount} di ${totalCount}`)}
          </span>
          <ChevronDown
            className={cn(
              "h-3 w-3 transition-transform duration-fast ease-smooth",
              open ? "rotate-180 text-ando-orange" : "text-ink-3",
            )}
            aria-hidden
          />
        </button>
      </PopoverTrigger>

      <PopoverContent
        align="end"
        sideOffset={8}
        // Radix Popover handles focus-trap + Esc + arrow-key roving when
        // `role="menu"` is wired on the inner list (see below).
        className={cn(
          "w-72 animate-el-in overflow-hidden rounded-md p-0 shadow-md",
          contentClassName,
        )}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-black/5 border-b px-3 py-2.5">
          <span className="font-semibold text-[10px] text-ink-3 uppercase tracking-[0.1em]">
            {triggerLabel}
          </span>
          <span className="text-[11px] text-ink-3 tabular-nums">
            {visibleActiveCount}/{totalCount}
          </span>
        </div>

        {/* All / None toggle — chip-pill pattern, same dims as SubTabChip */}
        <div className="flex items-center gap-1 px-2 pt-2">
          <button
            type="button"
            onClick={selectAll}
            disabled={allActive}
            className={cn(
              "flex-1 rounded-full px-3 py-[5px] font-medium text-[11px] leading-none transition-colors",
              allActive
                ? "bg-ando-orange/15 text-ando-orange-hover"
                : "bg-transparent text-ink-2 hover:bg-cream-deep hover:text-ink-1",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1",
              "disabled:cursor-default",
            )}
          >
            {resolvedAllLabel}
          </button>
          <button
            type="button"
            onClick={selectNone}
            disabled={noneActive}
            className={cn(
              "flex-1 rounded-full px-3 py-[5px] font-medium text-[11px] leading-none transition-colors",
              noneActive
                ? "bg-cream-deep text-ink-2"
                : "bg-transparent text-ink-2 hover:bg-cream-deep hover:text-ink-1",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1",
              "disabled:cursor-default",
            )}
          >
            {resolvedNoneLabel}
          </button>
        </div>

        {/* List */}
        <ul
          role="menu"
          aria-label={triggerLabel}
          className="max-h-[18rem] overflow-y-auto py-2"
        >
          {connections.map((option) => {
            const isActive = activeSet.has(option.id);
            return (
              <li key={option.id} role="presentation">
                <button
                  type="button"
                  role="menuitemcheckbox"
                  aria-checked={isActive}
                  onClick={() => toggle(option.id)}
                  className={cn(
                    "flex w-full items-center gap-2.5 px-3 py-1.5 text-left transition-colors",
                    "focus-visible:outline-none",
                    isActive
                      ? "bg-accent/[0.08] text-ink-1"
                      : "bg-transparent text-ink-1 hover:bg-cream-deep focus-visible:bg-cream-deep",
                  )}
                >
                  <ConnectionGlyph option={option} active={isActive} />
                  <span className="flex min-w-0 flex-1 flex-col">
                    <span
                      className={cn(
                        "truncate text-[12.5px]",
                        isActive ? "font-semibold" : "font-medium",
                      )}
                    >
                      {option.account_label}
                    </span>
                    {option.secondary_label ? (
                      <span className="truncate text-[11px] text-ink-3">
                        {option.secondary_label}
                      </span>
                    ) : null}
                  </span>
                  <Check
                    className={cn(
                      "h-4 w-4 shrink-0 text-accent transition-opacity",
                      isActive ? "opacity-100" : "opacity-0",
                    )}
                    strokeWidth={2.5}
                    aria-hidden
                  />
                </button>
              </li>
            );
          })}
        </ul>
      </PopoverContent>
    </Popover>
  );
};

ConnectionPicker.displayName = "ConnectionPicker";
